import { useState, useEffect } from "react";
import {
  LeftCircleOutlined,
  LoadingOutlined,
  PlusCircleOutlined,
  StopOutlined,
  PauseCircleOutlined
} from "@ant-design/icons";
import { Button, Col, Descriptions, Form, Modal, Row, Select, notification } from "antd";
import {
  useCancelBookingMutation,
  useCreateCustomerConnectLogForBookingMutation,
  useLazyGetBookingByIdQuery,
  useGetCustomerConnectLogsForBookingQuery,
  useOnHoldBookingMutation,
  useUpdateCustomerConnectLogForBookingMutation
} from "../../../redux/services/bookingAPI";
import ConnectLogTabel from "../ConnectLogTabel";
import { useAppSelector } from "../../../redux/hooks";
import { Link, useNavigate, useParams } from "react-router-dom";
import ConnectLogForm from "../ConnectLogForm";
import ErrorDetails from "../../../components/ErrorDetails";
import { localizeDateTime, localizeDate, customerAddress, getStatusText } from "../../../utils";
import { holdOptions, cancelOptions } from "../../../constants/helperConstants";

const ViewBooking = (props: any) => {
  const navigate = useNavigate();

  const params = useParams();
  const [form] = Form.useForm();

  const [api, contextHolder] = notification.useNotification();

  const [showModal, setShowModal] = useState(false);
  const [isEditMode, setEditMode] = useState(false);
  const [selectedCallLogData, setSelectedCallLogData] = useState({});

  const [getBookingById, { data, isLoading, refetch: refetchBookingById }] = useLazyGetBookingByIdQuery();
  const { data: customerConnectLogs, refetch: refetchCustomerConnectLogs } =
    useGetCustomerConnectLogsForBookingQuery(params.bookingId);

  const booking = data?.booking;

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showFullView, setShowFullView] = useState(false);
  const [modalType, setModalType] = useState("");

  const [cancelBooking, { isLoading: cancelBookingInProgress, isError: cancelBookingError }] =
    useCancelBookingMutation();

  const [onHoldBooking, { isLoading: onHoldBookingInProgress, isError: onHoldBookingError }] =
    useOnHoldBookingMutation();

  const [onCreateCustomerConnectLogForBooking] = useCreateCustomerConnectLogForBookingMutation();
  const [onUpdateCustomerConnectLogForBooking] = useUpdateCustomerConnectLogForBookingMutation();

  const handleCancelOrHold = (type: string) => {
    setModalType(type);
    setIsModalOpen(true);
  };

  const user = useAppSelector<any>((store) => store.auth.user);

  const isClient = user?.user_type === "client";

  useEffect(() => {
    if (params?.bookingId) {
      getBookingById(params.bookingId);
    }
  }, [params?.bookingId]);

  const onFinish = async (values: any) => {
    if (modalType === "HOLD") {
      try {
        await onHoldBooking({ id: params.bookingId, reason: values.on_hold_reason }).unwrap();
        api.success({
          message: "Booking marked as on hold operation successful",
          placement: "top"
        });
        form.resetFields();
        setIsModalOpen(false);
        refetchBookingById();
      } catch (error: any) {
        api.info({
          message: "Booking marked as on hold operation failed",
          description: <ErrorDetails errors={error?.data || []} />,
          placement: "top"
        });
      }
    }

    if (modalType === "CANCEL") {
      try {
        await cancelBooking({ id: params.bookingId, reason: values.cancel_reason }).unwrap();
        api.success({
          message: "Booking cancelled successfully",
          placement: "top"
        });
        form.resetFields();
        refetchBookingById();
        setIsModalOpen(false);
      } catch (error: any) {
        api.info({
          message: "Booking cancelled operation failed",
          description: <ErrorDetails errors={error?.data || []} />,
          placement: "top"
        });
      }
    }
  };

  const onAddCallLog = async (values: object, setLoading: any, form: any) => {
    try {
      setLoading(true);
      await onCreateCustomerConnectLogForBooking(values).unwrap();
      api.success({
        message: "Call log created for booking",
        placement: "top"
      });
      form.resetFields();
      refetchCustomerConnectLogs();
      setLoading(false);
      setShowModal(false);
    } catch (error: any) {
      api.info({
        message: "Create call log for booking operation failed",
        description: <ErrorDetails errors={error?.data || []} />,
        placement: "top"
      });
      setLoading(false);
    }
  };

  const onUpdateCallLog = async (values: object, setLoading: any, form: any) => {
    try {
      setLoading(true);
      await onUpdateCustomerConnectLogForBooking(values).unwrap();
      api.success({
        message: "Call log updated for booking",
        placement: "top"
      });
      form.resetFields();
      refetchCustomerConnectLogs();
      setLoading(false);
      setShowModal(false);
    } catch (error: any) {
      api.info({
        message: "Update call log for booking operation failed",
        description: <ErrorDetails errors={error?.data || []} />,
        placement: "top"
      });
      setLoading(false);
    }
  };

  const onUpdateCallLogModal = (callLog: any) => {
    setSelectedCallLogData(callLog);
    setEditMode(true);
    setShowModal(true);
  };

  const onUpdate = (callLog: any) => {
    setSelectedCallLogData(callLog);
    setEditMode(true);
    setShowModal(true);
  };

  const onFinishFailed = (errorInfo: any) => {
    console.log("Failed:", errorInfo);
  };

  const handleCancel = () => {
    setModalType("");
    setIsModalOpen(false);
  };

  const onGoBack = () => {
    navigate(-1);
  };

  return (
    <>
      {contextHolder}
      {!isLoading && data && (
        <div style={{ paddingBottom: 50 }}>
          <Row style={{ marginBottom: 20 }}>
            <Col
              sm={24}
              style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
            >
              <h2>Booking Details</h2>
              <Button onClick={onGoBack} size="middle" type="default" icon={<LeftCircleOutlined />}>
                Back
              </Button>
            </Col>
          </Row>
          {!isClient && (
            <Row style={{ marginBottom: 20 }}>
              <Col
                sm={24}
                style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
              >
                <div></div>
                <div style={{ display: "flex", gap: 5, alignItems: "center" }}>
                  <Link to={"create-survey"}>
                    <Button icon={<PlusCircleOutlined />}>Create Survey</Button>
                  </Link>
                  <Button icon={<StopOutlined />} onClick={() => handleCancelOrHold("CANCEL")}>
                    Cancel
                  </Button>
                  <Button icon={<PauseCircleOutlined />} onClick={() => handleCancelOrHold("HOLD")}>
                    Hold
                  </Button>
                  <Button icon={<PlusCircleOutlined />} onClick={() => setShowModal(true)}>
                    Create Call Connect Log
                  </Button>
                </div>
              </Col>
            </Row>
          )}

          <Descriptions
            title=""
            bordered
            column={1}
            labelStyle={{ fontWeight: 700, width: 250 }}
            size="middle"
          >
            {!isClient && (
              <Descriptions.Item label="eCB Booking Code">
                {data?.booking?.ecb_booking_code || "-"}
              </Descriptions.Item>
            )}
            <Descriptions.Item label="Charger Type">{data?.booking?.obc || "-"}</Descriptions.Item>
            <Descriptions.Item label="Dealer Email">
              {data?.booking?.dealer_email || "-"}
            </Descriptions.Item>
            <Descriptions.Item label="City Manager Email">
              {data?.booking?.city_manager_email || "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Zonal Manager Email">
              {data?.booking?.zonal_manager_email || "-"}
            </Descriptions.Item>
          </Descriptions>
          <br />

          <h3>Customer Details</h3>
          <Descriptions
            title=""
            bordered
            column={1}
            labelStyle={{ fontWeight: 700, width: 250 }}
            size="middle"
          >
            <Descriptions.Item label="Name">
              {data?.booking?.customer_name || "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Phone">
              {data?.booking?.contact_number || "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Booking Address">
              {data?.booking?.booking_address || "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Zone">{data?.booking?.zone?.name || "-"}</Descriptions.Item>
            <Descriptions.Item label="Address">{customerAddress(data?.booking)}</Descriptions.Item>
            {!isClient && (
              <Descriptions.Item label="EV City">
                {data?.booking?.city?.name || "-"}
              </Descriptions.Item>
            )}
            <Descriptions.Item label="Customer city of Survey/Installation">
              {data?.booking?.customer_city || "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Survey Confirmed">
              {data?.booking?.survey_done_same_day ? "YES" : "NO"}
            </Descriptions.Item>
          </Descriptions>

          <h3>Vehicle Details</h3>
          <Descriptions
            title=""
            bordered
            column={1}
            labelStyle={{ fontWeight: 700, width: 250 }}
            size="middle"
          >
            <Descriptions.Item label="Dealer">
              {data?.booking?.dealer_name || "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Dealer Loc.">
              {data?.booking?.dealer_location || "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Brand Model">
              {data?.booking?.brand_model?.name || "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Model Color">
              {data?.booking?.model_colour || "-"}
            </Descriptions.Item>
          </Descriptions>

          <h3>Timelines</h3>
          <Descriptions
            title=""
            bordered
            column={1}
            labelStyle={{ fontWeight: 700, width: 250 }}
            size="middle"
          >
            <Descriptions.Item label="Status">{getStatusText(data?.booking)}</Descriptions.Item>
            <br />
            <Descriptions.Item label="Scheduled Date - Time">
              {data?.booking?.scheduled_install_date_time
                ? localizeDateTime(data?.booking?.scheduled_install_date_time)
                : "-"}
            </Descriptions.Item>
            <br />
            {!isClient && (
              <Descriptions.Item label="Brand Booking Date">
                {data?.booking?.brand_booking_date
                  ? localizeDate(data?.booking?.brand_booking_date)
                  : "-"}
              </Descriptions.Item>
            )}
            <Descriptions.Item label="eCB Receive Date">
              {data?.booking?.ecb_receive_date
                ? localizeDate(data?.booking?.ecb_receive_date)
                : "-"}
            </Descriptions.Item>
            <Descriptions.Item label="eCB Booking Date">
              {data?.booking?.ecb_booking_date
                ? localizeDate(data?.booking?.ecb_booking_date)
                : "-"}
            </Descriptions.Item>
            <br />
            <Descriptions.Item label="First Contact">
              {data?.booking?.first_contact_at
                ? localizeDateTime(data?.booking?.first_contact_at)
                : "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Created At">
              {data?.booking?.created_at ? localizeDateTime(data?.booking?.created_at) : "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Next Follow Up">
              {data?.booking?.next_follow_up_at
                ? localizeDateTime(data?.booking?.next_follow_up_at)
                : "-"}
            </Descriptions.Item>
            <br />
            <Descriptions.Item label="Last Updated">
              {data?.booking?.updated_at ? localizeDateTime(data?.booking?.updated_at) : "-"}
            </Descriptions.Item>
          </Descriptions>

          {!showFullView && (
            <Button type="link" onClick={() => setShowFullView(true)} style={{ marginTop: "20px" }}>
              Show More
            </Button>
          )}

          {showFullView && (
            <>
              <br />
              <Descriptions
                title=""
                bordered
                column={1}
                labelStyle={{ fontWeight: 700, width: 250 }}
                size="middle"
              >
                {!isClient && (
                  <>
                    <Descriptions.Item label="Brand Booking Code">
                      {data?.booking?.brand_booking_code || "-"}
                    </Descriptions.Item>
                    <Descriptions.Item label="Brand Booking Status">
                      {data?.booking?.brand_booking_status || "-"}
                      {data?.booking?.brand_booking_status &&
                        data?.booking?.brand_booking_status === "cancelled" && (
                          <>
                            {data?.booking?.brand_cancelled_at && (
                              <p style={{ margin: 0 }}>
                                Time: {localizeDateTime(data?.booking?.brand_cancelled_at)}
                              </p>
                            )}
                            {data?.booking?.brand_cancel_reason && (
                              <p style={{ margin: 0 }}>
                                Reason: {data?.booking?.brand_cancel_reason}
                              </p>
                            )}
                          </>
                        )}
                    </Descriptions.Item>
                  </>
                )}
                <Descriptions.Item label="Cust. Alt. Contact">
                  {data?.booking?.alt_contact_number || "-"}
                </Descriptions.Item>
                <Descriptions.Item label="Cust. Email">
                  {data?.booking?.email || "-"}
                </Descriptions.Item>
                <br />
                <Descriptions.Item label="Record - Cust. Ok?">
                  {data?.booking?.customer_ok_with_recording ? "YES" : "NO"}
                </Descriptions.Item>
                <br />
                <Descriptions.Item label="Model Desc.">
                  {data?.booking?.model_description || "-"}
                </Descriptions.Item>
                <Descriptions.Item label="Variant">
                  {data?.booking?.model_variant || "-"}
                </Descriptions.Item>
                <Descriptions.Item label="Int Color">
                  {data?.booking?.model_int_color || "-"}
                </Descriptions.Item>
                <Descriptions.Item label="Ext Color">
                  {data?.booking?.model_ext_color || "-"}
                </Descriptions.Item>

                <br />

                <Descriptions.Item label="Latitude">
                  {data?.booking?.latitude || "-"}
                </Descriptions.Item>
                <Descriptions.Item label="Longitude">
                  {data?.booking?.longitude || "-"}
                </Descriptions.Item>
                <Descriptions.Item label="Outstation for installer">
                  {data?.booking?.is_outstation ? "YES" : "NO"}
                </Descriptions.Item>
                <Descriptions.Item label="Outstation for client">
                  {data?.booking?.is_outstation_for_client ? "YES" : "NO"}
                </Descriptions.Item>

                <Descriptions.Item label="Site POC Name">
                  {data?.booking?.site_poc_name || "-"}
                </Descriptions.Item>
                <Descriptions.Item label="Site POC Contact">
                  {data?.booking?.site_poc_contact || "-"}
                </Descriptions.Item>

                <br />

                <Descriptions.Item label="Is this client (OEM) paid ?">
                  {data?.booking?.customer_billing ? "Yes" : "No"}
                </Descriptions.Item>

                <Descriptions.Item label="Planned Start Date">
                  {data?.booking?.planned_start_date || "-"}
                </Descriptions.Item>
                <Descriptions.Item label="Complaint Code">
                  {data?.booking?.complaint_code || "-"}
                </Descriptions.Item>
                <Descriptions.Item label="Complaint Given By">
                  {data?.booking?.complaint_given_by || "-"}
                </Descriptions.Item>
                <Descriptions.Item label="Product Type">
                  {data?.booking?.product_type || "-"}
                </Descriptions.Item>
                <Descriptions.Item label="SR Type">
                  {data?.booking?.sr_type || "-"}
                </Descriptions.Item>
                <Descriptions.Item label="Email 2">
                  {data?.booking?.email2 || "-"}
                </Descriptions.Item>
                <Descriptions.Item label="Source">{data?.booking?.source || "-"}</Descriptions.Item>
                <Descriptions.Item label="Created By">
                  {data?.booking?.created_by || "-"}
                </Descriptions.Item>
                <Descriptions.Item label="Brand Initiated Date">
                  {data?.booking?.brand_initiated_date || "-"}
                </Descriptions.Item>
                <Descriptions.Item label="Brand Unique ID">
                  {data?.booking?.brand_unique_id || "-"}
                </Descriptions.Item>
                <Descriptions.Item label="Dealer Code">
                  {data?.booking?.dealer_code || "-"}
                </Descriptions.Item>
                <Descriptions.Item label="Dealer Branch Code">
                  {data?.booking?.dealer_branch_code || "-"}
                </Descriptions.Item>
                <Descriptions.Item label="Brand Reshedule ID">
                  {data?.booking?.brand_reschedule_id || "-"}
                </Descriptions.Item>
                <Descriptions.Item label="Appointment Taken On">
                  {data?.booking?.appointment_taken_on
                    ? localizeDateTime(data?.booking?.appointment_taken_on)
                    : "-"}
                </Descriptions.Item>
                <Descriptions.Item label="Brand Modified By">
                  {data?.booking?.brand_modified_by || "-"}
                </Descriptions.Item>
                <Descriptions.Item label="Brand Modified Date">
                  {data?.booking?.brand_modified_date
                    ? localizeDateTime(data?.booking?.brand_modified_date)
                    : "-"}
                </Descriptions.Item>
                <Descriptions.Item label="Brand City Code">
                  {data?.booking?.brand_city_code || "-"}
                </Descriptions.Item>
                <Descriptions.Item label="Brand Booking Code">
                  {data?.booking?.brand_booking_code || "-"}
                </Descriptions.Item>
                <Descriptions.Item label="PI Survey Send Date">
                  {data?.booking?.pi_survey_send_date ? localizeDateTime(data?.booking?.pi_survey_send_date) : "-"}
                </Descriptions.Item>
                <Descriptions.Item label="PI Installation Send Date">
                  {data?.booking?.pi_installation_send_date ? localizeDateTime(data?.booking?.pi_installation_send_date) : "-"}
                </Descriptions.Item>
              </Descriptions>
            </>
          )}

          {showFullView && (
            <Button
              type="link"
              onClick={() => setShowFullView(false)}
              style={{ marginTop: "20px" }}
            >
              Show Less
            </Button>
          )}

          <ConnectLogTabel
            onUpdate={onUpdate}
            title="Booking customer connect logs"
            data={customerConnectLogs?.customer_connect_logs || []}
            msil={data?.booking?.brand?.send_reports_to_api}
            onUpdateCallLogModal={onUpdateCallLogModal}
          />
        </div>
      )}

      {isLoading && (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100vh"
          }}
        >
          <LoadingOutlined style={{ fontSize: 24 }} spin />
        </div>
      )}

      <>
        <Modal
          title={`${modalType === "HOLD" ? "Put Booking on Hold" : "Cancel Booking"}`}
          open={isModalOpen}
          onCancel={handleCancel}
          footer={[
            <Button key="cancel" onClick={handleCancel}>
              Cancel
            </Button>,
            <Button form="myForm" key="submit" htmlType="submit" type="primary">
              Submit
            </Button>
          ]}
        >
          <Form
            name="myForm"
            form={form}
            labelCol={{ span: 24 }}
            wrapperCol={{ span: 24 }}
            style={{ maxWidth: 600 }}
            initialValues={{ remember: true }}
            onFinish={onFinish}
            onFinishFailed={onFinishFailed}
            autoComplete="off"
          >
            <Form.Item
              label={modalType === "HOLD" ? "Reason for puting on hold" : "Reason for cancel"}
              name={modalType === "HOLD" ? "on_hold_reason" : "cancel_reason"}
              rules={[{ required: true, message: "Please input reason!" }]}
            >
              <Select
                placeholder="Select status"
                showSearch
                optionFilterProp="children"
                filterOption={(inputValue, option: any) =>
                  option.label.toLowerCase().includes(inputValue.toLowerCase())
                }
                options={modalType === "HOLD" ? holdOptions : cancelOptions}
              ></Select>
            </Form.Item>
          </Form>
        </Modal>

        {showModal && (
          <ConnectLogForm
            assignment={booking}
            title={isEditMode ? "Update customer connect logs" : "Create customer connect logs"}
            isOpen={showModal}
            msil={data?.booking?.brand?.send_reports_to_api}
            nextFollowUpRequired={data?.booking?.brand?.next_follow_up_required}
            handleCancel={() => {
              setShowModal(false);
              setEditMode(false);
            }}
            isEditMode={isEditMode}
            onAddCallLog={!isEditMode && onAddCallLog}
            onUpdateCallLog={isEditMode && onUpdateCallLog}
            selectedCallLogData={isEditMode && selectedCallLogData}
            booking
          />
        )}
      </>
    </>
  );
};

export default ViewBooking;
